import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Columns2, Eye, PenLine, X, SplitSquareHorizontal, RefreshCw } from 'lucide-react';
import type { NoteDetail } from '@cortex/shared';
import { Api, ApiError } from '../../lib/api';
import { useNote, qk } from '../../hooks/useVault';
import { useOpenLink } from '../../hooks/useOpenLink';
import { useUi, type Pane } from '../../store/ui';
import { Editor } from './Editor';
import { Preview } from './Preview';
import { ConflictDialog } from './ConflictDialog';
import { TypeDot } from '../ui/TypeDot';

type SaveState = 'clean' | 'dirty' | 'saving' | 'saved' | 'error';

export function NoteView({ pane, active }: { pane: Pane; active: boolean }) {
  const { noteId } = pane;
  const note = useNote(noteId);
  const qc = useQueryClient();
  const ui = useUi();
  const draft = useUi((s) => (noteId ? s.drafts[noteId] : undefined));
  const setDraft = useUi((s) => s.setDraft);
  const markSaved = useUi((s) => s.markSaved);
  const openLink = useOpenLink();
  const [saveState, setSaveState] = useState<SaveState>('clean');
  const [conflict, setConflict] = useState<{ disk: string; diskHash: string; mine: string } | null>(null);
  const [diskChanged, setDiskChanged] = useState(false);
  const timer = useRef<number | null>(null);
  const savingRef = useRef(false);

  // Seed the draft from the server the first time (or when the server copy changes and we are clean).
  useEffect(() => {
    const n = note.data;
    if (!n || !noteId) return;
    if (!draft) {
      setDraft(noteId, { raw: n.raw, baseHash: n.contentHash, dirty: false }, 'external');
      setSaveState('clean');
      return;
    }
    if (n.contentHash !== draft.baseHash) {
      if (!draft.dirty) {
        setDraft(noteId, { raw: n.raw, baseHash: n.contentHash, dirty: false }, 'external');
        setDiskChanged(false);
      } else {
        setDiskChanged(true);
      }
    }
  }, [note.data, noteId, draft, setDraft]);

  const save = useCallback(
    async (rawOverride?: string, hashOverride?: string) => {
      if (!noteId) return;
      const d = useUi.getState().drafts[noteId];
      if (!d || savingRef.current) return;
      const raw = rawOverride ?? d.raw;
      const base = hashOverride ?? d.baseHash;
      if (!d.dirty && rawOverride === undefined) return;
      savingRef.current = true;
      setSaveState('saving');
      try {
        const updated = await Api.notes.update(noteId, raw, base);
        markSaved(noteId, raw, updated.contentHash);
        qc.setQueryData<NoteDetail>(qk.note(noteId), updated);
        qc.invalidateQueries({ queryKey: qk.tree });
        qc.invalidateQueries({ queryKey: ['backlinks'] });
        setSaveState(useUi.getState().drafts[noteId]?.dirty ? 'dirty' : 'saved');
        setDiskChanged(false);
        setConflict(null);
      } catch (err) {
        if (err instanceof ApiError && err.status === 409) {
          setConflict({ disk: String(err.body.current ?? ''), diskHash: String(err.body.currentHash ?? ''), mine: raw });
          setSaveState('error');
        } else {
          setSaveState('error');
          ui.toast(`Save failed: ${(err as Error).message}`, 'crit');
        }
      } finally {
        savingRef.current = false;
      }
    },
    [noteId, markSaved, qc, ui],
  );

  const onChange = useCallback(
    (raw: string) => {
      if (!noteId) return;
      const d = useUi.getState().drafts[noteId];
      if (d && d.raw === raw) return;
      setDraft(noteId, { raw, dirty: true }, 'editor');
      setSaveState('dirty');
      if (timer.current) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => void save(), 1200);
    },
    [noteId, setDraft, save],
  );

  // Flush pending autosave on unmount / note switch.
  useEffect(
    () => () => {
      if (timer.current) window.clearTimeout(timer.current);
      const d = noteId ? useUi.getState().drafts[noteId] : undefined;
      if (d?.dirty) void save();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [noteId],
  );

  const reloadFromDisk = () => {
    if (!noteId) return;
    ui.dropDraft(noteId);
    setDiskChanged(false);
    qc.invalidateQueries({ queryKey: qk.note(noteId) });
  };

  const notify = useCallback((m: string, k?: 'info' | 'warn' | 'crit') => ui.toast(m, k), [ui]);
  const docKey = useMemo(() => `${noteId ?? 'none'}:${note.data?.path ?? ''}`, [noteId, note.data?.path]);

  if (!noteId) return <EmptyPane />;
  if (note.isError) {
    return (
      <div className="pane" onMouseDown={() => ui.setActivePane(pane.id)}>
        <div className="pane-empty">
          <div>Note not found.</div>
          <button className="ghost" onClick={() => ui.closePane(pane.id)}>Close</button>
        </div>
      </div>
    );
  }
  if (!note.data || !draft) return <div className="pane" />;
  const n = note.data;

  return (
    <div className={`pane${active ? ' active-pane' : ''}`} onMouseDown={() => ui.setActivePane(pane.id)}>
      <div className="pane-head">
        <TypeDot type={n.type} />
        <span className="title" title={n.title}>{n.title}</span>
        <span className="path" title={n.path}>{n.path}</span>
        {n.schemaWarnings.length > 0 && (
          <button className="chip warn" title={n.schemaWarnings.map((w) => `${w.path} ${w.message}`).join('\n')} onClick={() => ui.setDockTab('properties')}>
            {n.schemaWarnings.length} schema
          </button>
        )}
        <span className="spacer" />
        <span className={`save-state ${saveState}`}>
          {saveState === 'dirty' ? '● unsaved' : saveState === 'saving' ? 'saving…' : saveState === 'saved' ? 'saved' : saveState === 'error' ? 'conflict' : ''}
        </span>
        <div className="modes" role="group" aria-label="View mode">
          <button className={pane.mode === 'edit' ? 'on' : ''} onClick={() => ui.setPaneMode(pane.id, 'edit')} title="Edit (Ctrl+E cycles)"><PenLine size={12} /></button>
          <button className={pane.mode === 'split' ? 'on' : ''} onClick={() => ui.setPaneMode(pane.id, 'split')} title="Source + preview"><Columns2 size={12} /></button>
          <button className={pane.mode === 'preview' ? 'on' : ''} onClick={() => ui.setPaneMode(pane.id, 'preview')} title="Preview"><Eye size={12} /></button>
        </div>
        <button className="icon" title="Open in split pane (Ctrl+\)" onClick={() => ui.splitPane()}><SplitSquareHorizontal size={14} /></button>
        <button className="icon" title="Close pane" onClick={() => ui.closePane(pane.id)}><X size={14} /></button>
      </div>
      {diskChanged && (
        <div className="pane-banner">
          <span className="light warn" />
          This note changed on disk while you were editing. Your changes are kept; saving will show a merge view.
          <button className="ghost" style={{ marginLeft: 'auto' }} onClick={reloadFromDisk}><RefreshCw size={12} /> Discard mine, reload</button>
        </div>
      )}
      <div className={`pane-body${pane.mode === 'split' ? ' split' : ''}`}>
        {(pane.mode === 'edit' || pane.mode === 'split') && (
          <Editor
            docKey={docKey}
            value={draft.raw}
            externalVersion={draft.version}
            onChange={onChange}
            onSave={() => void save()}
            onOpenLink={(t, o) => void openLink(t, o)}
            notePath={n.path}
            notify={notify}
            autoFocus={active}
          />
        )}
        {(pane.mode === 'preview' || pane.mode === 'split') && <Preview raw={draft.raw} noteId={noteId} onOpenLink={(t, o) => void openLink(t, o)} />}
      </div>
      {conflict && (
        <ConflictDialog
          path={n.path}
          disk={conflict.disk}
          mine={conflict.mine}
          onCancel={() => setConflict(null)}
          onUseDisk={() => {
            setDraft(noteId, { raw: conflict.disk, baseHash: conflict.diskHash, dirty: false }, 'external');
            setConflict(null);
            setSaveState('clean');
            setDiskChanged(false);
            qc.invalidateQueries({ queryKey: qk.note(noteId) });
          }}
          onOverwrite={(merged) => {
            setDraft(noteId, { raw: merged, baseHash: conflict.diskHash, dirty: true }, 'external');
            void save(merged, conflict.diskHash);
          }}
        />
      )}
    </div>
  );
}

function EmptyPane() {
  const openPalette = useUi((s) => s.openPalette);
  return (
    <div className="pane">
      <div className="pane-empty">
        <div className="wordmark" style={{ fontSize: 18 }}><span className="caret">▸</span> Cortex</div>
        <div>No note open.</div>
        <div className="kbd-row">
          <span><span className="kbd">Ctrl</span> <span className="kbd">K</span> search</span>
          <span><span className="kbd">Ctrl</span> <span className="kbd">N</span> new note</span>
          <span><span className="kbd">Ctrl</span> <span className="kbd">\</span> split</span>
        </div>
        <button className="ghost" onClick={() => openPalette()}>Open palette</button>
      </div>
    </div>
  );
}
